import { UserAvatar } from './UserAvatar';
import { cn } from '@/lib/utils';
import type { AuditAssignee, User } from '@/types/asset-audit';

interface AssigneeListProps {
  readonly assignees: AuditAssignee[] | User[];
  readonly maxVisible?: number;
  readonly size?: 'xs' | 'sm' | 'md' | 'lg';
  readonly className?: string;
  readonly onShowAll?: () => void;
}

function isAuditAssignee(assignee: AuditAssignee | User): assignee is AuditAssignee {
  return 'userId' in assignee;
}

export function AssigneeList({
  assignees,
  maxVisible = 3,
  size = 'sm',
  className,
  onShowAll,
}: Readonly<AssigneeListProps>) {
  const visibleAssignees = assignees.slice(0, maxVisible);
  const remainingCount = assignees.length - maxVisible;

  if (assignees.length === 0) {
    return (
      <div className={cn('text-sm text-muted-foreground', className)}>
        No assignees
      </div>
    );
  }

  return (
    <div className={cn('flex items-center', className)}>
      <div className="flex gap-1">
        {visibleAssignees.map((assignee) => {
          const key = isAuditAssignee(assignee) ? assignee.userId : assignee.id;
          const name = isAuditAssignee(assignee) ? assignee.userName : assignee.name;
          const avatarUrl = isAuditAssignee(assignee) ? assignee.userAvatar : assignee.avatar;
          return (
            <div key={key}>
              <UserAvatar
                name={name}
                avatarUrl={avatarUrl}
                size={size}
              />
            </div>
          );
        })}
        {remainingCount > 0 && (
          <button
            onClick={onShowAll}
            className={cn(
              'flex items-center justify-center rounded-full bg-muted text-muted-foreground font-medium hover:bg-muted/80 transition-colors',
              size === 'xs' && 'h-6 w-6 text-xs',
              size === 'sm' && 'h-8 w-8 text-sm',
              size === 'md' && 'h-10 w-10 text-base',
              size === 'lg' && 'h-12 w-12 text-lg'
            )}
            title={`${remainingCount} more assignee${remainingCount > 1 ? 's' : ''}`}
          >
            +{remainingCount}
          </button>
        )}
      </div>
    </div>
  );
}
